/**
 * Density tokens for the inline tool-confirmation forms.
 *
 * These forms are the app's densest data-entry surface, and every one of them
 * renders inside a chat bubble that is ~194px wide on a phone. Two constraints
 * collide there:
 *
 *   1. An <input> under 16px triggers iOS Safari's focus auto-zoom, which does
 *      NOT zoom back out. `ui/input.tsx` guards this with `text-base md:text-sm`;
 *      these tokens deliberately break at `sm:` (640px) instead, because that
 *      is THIS app's declared posture boundary (`MOBILE_MAX_WIDTH_PX`). Keeping
 *      `md:` opened a third, undocumented posture: between 640 and 767 the rail
 *      rendered and the panel docked — `useIsMobile()` said desktop — while every
 *      control here was still 44px and 16px.
 *      — but `tailwind-merge` treats `text-sm` and `md:text-sm` as different
 *      groups, so a call-site `text-sm` silently wins at EVERY width.
 *   2. A 32px (`h-8`) control is below the 44px touch-target minimum.
 *
 * One token fixes both and keeps desktop density identical to before. Import
 * this instead of hand-writing `h-8 text-sm`; the guard test in
 * `__tests__/lib/formDensity.test.ts` fails the build if you don't.
 */

/** Standard dense text/number input inside a confirmation form. */
export const DENSE_INPUT_CLASS = 'h-11 text-base sm:h-8 sm:text-sm';

/** Monospace variant — ref handles, ids. Denser still on desktop. */
export const DENSE_INPUT_MONO_CLASS = 'h-11 text-base font-mono sm:h-7 sm:text-xs';

/**
 * Extra-dense variant, for the sites that were already `text-xs` on desktop.
 *
 * `DENSE_INPUT_CLASS` resolves to `md:text-sm`, so pointing a `h-8 text-xs`
 * site at it silently enlarges that input from 12px to 14px on DESKTOP — which
 * breaks the invariant this whole branch rests on ("desktop is unchanged").
 * This token fixes the phone side without touching the desktop side at all.
 */
export const DENSE_INPUT_XS_CLASS = 'h-11 text-base sm:h-8 sm:text-xs';
